import {
  createSlice,
  createAsyncThunk,
  AsyncThunk,
  AnyAction
} from '@reduxjs/toolkit';
import type { FilerBrowse, File } from 'src/types/Archive';
import { FileTypes } from 'src/views/archive/types';
import { RootState } from 'src/store';
import { urls } from '../utils/api';
import { protectedAxios as axios } from '../utils/auth';

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;

const isRejectedAction = (action: AnyAction): action is RejectedAction =>
  action.type.endsWith('/rejected');
const isPendingAction = (action: AnyAction): action is PendingAction =>
  action.type.endsWith('/pending');

interface ArchiveState {
  currentFilerBrowse: FilerBrowse;
  currentFile: File;
  error: any;
  loading: boolean;
}

const initialState: ArchiveState = {
  currentFilerBrowse: {
    folders: [],
    files: [],
    folder: null
  },
  currentFile: null,
  error: null,
  loading: false
};

type IdType = string | number;
type FilerBrowseReadProps = {
  folder?: IdType;
  search?: string;
  ordering?: string;
};
export const filerBrowseRead = createAsyncThunk(
  'archive/fetchByIdStatus',
  async ({ folder, search, ordering }: FilerBrowseReadProps, { getState }) => {
    try {
      const { archiveFilters: { createdAtFrom, createdAtTo, fileType } } = getState() as RootState;
      const response = await axios.get(urls.filerBrowseRead, { params: {
        folder,
        created_at__gte: createdAtFrom,
        created_at__lte: createdAtTo,
        type: fileType,
        search,
        ordering
      } });
      return response.data;
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

type FilerUploadProps = {
  files: any;
  folder?: any;
  type: FileTypes;
};
export const filerUpload = createAsyncThunk(
  'archive/filerUpload',
  async ({ files, folder, type }: FilerUploadProps) => {
    try {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('folder', folder ?? '');
      formData.append('type', type ?? '');

      const response = await axios.post(urls.filerUpload, formData);

      return response.data;
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

type FilerFolderCreateProps = {
  name: string;
  parent?: IdType;
};
export const filerFolderCreate = createAsyncThunk(
  'archive/filerFolderCreate',
  async ({ name, parent }: FilerFolderCreateProps) => {
    try {
      const response = await axios.post(urls.filerFolderCreate, {
        name,
        parent: parent ?? ''
      });
      return response.data;
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

type FilerFileDeleteProps = {
  id: IdType;
};
export const filerFileDelete = createAsyncThunk(
  'archive/filerFileDelete',
  async ({ id }: FilerFileDeleteProps) => {
    try {
      await axios.delete(`${urls.filerFile(id)}?id=${id}`);
      return { id };
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

type FilerFolderDeleteProps = {
  id: IdType;
};
export const filerFolderDelete = createAsyncThunk(
  'archive/filerFolderDelete',
  async ({ id }: FilerFolderDeleteProps) => {
    try {
      await axios.delete(`${urls.filerFolder(id)}?id=${id}`);
      return { id };
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

type FilerFileGetProps = {
  id: IdType;
};
export const filerFileGet = createAsyncThunk(
  'archive/filerFileGet',
  async ({ id }: FilerFileGetProps) => {
    try {
      const response = await axios.patch(`${urls.filerFile(id)}?id=${id}`);
      return response.data;
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

type FilerFolderUpdateProps = {
  id: IdType;
  name: string;
  parent?: IdType;
};
export const filerFolderUpdate = createAsyncThunk(
  'archive/filerFolderUpdate',
  async ({ id, name, parent }: FilerFolderUpdateProps) => {
    try {
      const response = await axios.put(`${urls.filerFolder(id)}?id=${id}`, {
        name,
        parent: parent ?? ''
      });
      return response.data;
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

type FilerFileUpdateProps = {
  id: IdType;
  name: string;
  folder?: IdType;
  type?: FileTypes;
};
export const filerFileUpdate = createAsyncThunk(
  'archive/filerFileUpdate',
  async ({ id, name, folder, type }: FilerFileUpdateProps) => {
    try {
      const response = await axios.put(`${urls.filerFile(id)}?id=${id}`, {
        name,
        folder: folder ?? '',
        type
      });
      return response.data;
    } catch (e) {
      const { data } = e.response;
      throw new Error(data[Object.keys(data)[0]]);
    }
  }
);

const slice = createSlice({
  name: 'archive',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(filerBrowseRead.fulfilled, (state, { payload }) => {
      state.currentFilerBrowse = payload;
      state.loading = false;
    });
    builder.addCase(filerUpload.fulfilled, (state, { payload }) => {
      state.currentFilerBrowse = payload;
      state.loading = false;
    });
    builder.addCase(filerFolderCreate.fulfilled, (state, { payload }) => {
      state.currentFilerBrowse.folders.push(payload);
      state.loading = false;
    });
    builder.addCase(filerFileDelete.fulfilled, (state, { payload }) => {
      const { id } = payload;

      state.currentFilerBrowse.files = state.currentFilerBrowse.files.filter(
        (file) => file.id !== id
      );

      state.loading = false;
    });
    builder.addCase(filerFolderDelete.fulfilled, (state, { payload }) => {
      const { id } = payload;

      state.currentFilerBrowse.folders = state.currentFilerBrowse.folders.filter(
        (folder) => folder.id !== id
      );

      state.loading = false;
    });
    builder.addCase(filerFileUpdate.fulfilled, (state, { payload }) => {
      const { id } = payload;
      state.currentFile = payload;
      state.currentFilerBrowse.files = state.currentFilerBrowse.files.map(
        (file) => {
          if (file.id === id) {
            return payload;
          }
          return file;
        }
      );

      state.loading = false;
    });

    builder.addCase(filerFolderUpdate.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(filerFileGet.fulfilled, (state, { payload }) => {
      state.currentFile = payload;
      state.loading = false;
    });
    builder.addMatcher(isRejectedAction, (state, { error }) => {
      state.error = error;
      state.loading = false;
    });
    builder.addMatcher(isPendingAction, (state) => {
      state.error = null;
      state.loading = true;
    });
  }
});

export const { clearError } = slice.actions;
export const { reducer } = slice;
export default slice;
